import React, { useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FormGenerator, ScriptValueDialog } from '@microservices/wiskey-react-components'
import { Box, Typography } from '@mui/material'

import { AutocompleteOption, MainObjectTypeEnum } from '@types'

import { ModalRefPathPicker } from '@gantt/components/GanttCreateOrEdit/components/ModalRefPathPicker'

import {
  getMainObjectGroupTitleScriptConfig,
  getMainObjectGroupTitleScriptLanguage,
} from '../../helpers'
import { PageContext } from '../../TimeLineCreateOrEdit'

export const MainObjectConfiguration = () => {
  const { t } = useTranslation()

  const { inputs, state, handlers, data } = useContext(PageContext)

  const { mainObjectInputs, mainObjectGroupInputs } = inputs

  const {
    mainObjectShowGroupTitleScriptDialog,
    watchMainObjectGroupTitleScript,
    watchMainObjectGroupTitleType,
    watchObjectXObject,
    watchMainObjectType,
    watchObjectYObject,
    watchMainObjectGroupField,
    watchMainObjectGroupFieldKey,
  } = state

  const { objectXSelectedObjectFields, objectYSelectedObjectFields } = data

  const {
    handleChangeMainObjectShowGroupTitleScriptDialog,
    handleChangeMainObjectGroupTitleScript,
  } = handlers

  const objectForPicker = useMemo(() => {
    if (watchMainObjectType === MainObjectTypeEnum.X) {
      return watchObjectXObject
    }

    return watchObjectYObject
  }, [watchMainObjectType, watchObjectYObject, watchObjectXObject])

  const groupTitleScriptConfig = useMemo(
    () =>
      getMainObjectGroupTitleScriptConfig({
        watchMainObjectType,
        watchObjectXObject,
        watchObjectYObject,
        objectXFields: objectXSelectedObjectFields,
        objectYFields: objectYSelectedObjectFields,
      }),
    [
      watchMainObjectType,
      watchObjectXObject,
      watchObjectYObject,
      objectXSelectedObjectFields,
      objectYSelectedObjectFields,
    ]
  )

  return (
    <Box display='flex' flexDirection='column' p={2}>
      <Typography variant='h5'>{t('ganttCreate.mainObjectForm.mainObject')}</Typography>

      <FormGenerator inputs={mainObjectInputs} />
      {Boolean(objectForPicker?.id) && (
        <Box component='fieldset' mb={1}>
          <legend>{t('timeLinesCreateOrEdit.mainObject.grouping')}</legend>
          <ModalRefPathPicker
            hasClearInInput
            isRequired
            refPickAllowed
            embeddedObjectPickerControllerName={'mainObject.groupField'}
            isDisabled={false}
            label={t(`timeLinesCreateOrEdit.mainObject.groupField`)}
            name={'mainObject.groupFieldKey'}
            object={objectForPicker as AutocompleteOption<string>}
            pickerName={''}
          />
          {watchMainObjectGroupFieldKey && (
            <ModalRefPathPicker
              hasClearInInput
              isRequired
              embeddedObjectPickerControllerName={'mainObject.groupTitleDisplayField'}
              isDisabled={!watchMainObjectGroupField?.length}
              label={t(`timeLinesCreateOrEdit.mainObject.groupTitleDisplayField`)}
              name={'mainObject.groupTitleDisplayFieldKey'}
              object={objectForPicker as AutocompleteOption<string>}
              pickerName={''}
            />
          )}
          <FormGenerator inputs={mainObjectGroupInputs} mainGridSpacing={0} />
        </Box>
      )}
      {mainObjectShowGroupTitleScriptDialog && (
        <ScriptValueDialog
          noValidate
          isShow
          language={getMainObjectGroupTitleScriptLanguage(watchMainObjectGroupTitleType?.id)}
          value={watchMainObjectGroupTitleScript}
          {...groupTitleScriptConfig}
          onClose={() => handleChangeMainObjectShowGroupTitleScriptDialog(false)}
          onSave={handleChangeMainObjectGroupTitleScript}
        />
      )}
    </Box>
  )
}
